import React, { useState } from "react";

const Chip = ({ text, color }) => (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
        {text}
    </span>
);

const InfoRow = ({ label, value }) => (
    <div className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide shrink-0 w-28">
            {label}
        </span>
        <span className="text-sm text-gray-800 text-right break-words">
            {value || "—"}
        </span>
    </div>
);

const SectionBlock = ({ title, children }) => (
    <div>
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
            {title}
        </h3>
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            {children}
        </div>
    </div>
);

const ChipGroup = ({ items, color, emptyText }) => (
    items?.length
        ? <div className="flex flex-wrap gap-1.5">
            {items.map(item => (
                <Chip key={item} text={item} color={color} />
            ))}
        </div>
        : <span className="text-sm text-gray-400">{emptyText}</span>
);

const isImage = (url) => {
    const ext = url.split("?")[0].split(".").pop().toLowerCase();

    return [
        "jpg",
        "jpeg",
        "png",
        "webp",
        "gif"
    ].includes(ext);
};

const isPdf = (url) => {
    return url.toLowerCase().includes(".pdf");
};

const getFileName = (url) => {
    return url.split("/").pop().replace(/^uploadfile-\d+-/, "");
};

const CampaignDetailsModal = ({ campaign, onClose }) => {
    const [showAllCampaigns, setShowAllCampaigns] = useState(false);
    if (!campaign) return null;
    const visibleCampaigns = showAllCampaigns ? (campaign.campaign_names || []) : (campaign.campaign_names || []).slice(0, 8);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Campaign Details</h2>
                        <p className="text-xs text-gray-400 mt-0.5">
                            Review campaign information and email preview
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition text-xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Body — scrollable */}
                <div className="flex-1 overflow-y-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
                        {/* Left column */}
                        <div className="lg:col-span-4 p-6 space-y-5">
                            <SectionBlock title="Summary">
                                <InfoRow label="Subject" value={campaign.subject} />
                                <InfoRow label="Segment" value={campaign.segment} />
                                <InfoRow
                                    label="Created"
                                    value={new Date(campaign.created_at).toLocaleString("en-US", {
                                        month: "short", day: "numeric",
                                        year: "numeric", hour: "2-digit", minute: "2-digit"
                                    })}
                                />
                                <InfoRow label="Attachments" value={campaign.attachments_count} />
                            </SectionBlock>
                            <SectionBlock title="Categories">
                                <ChipGroup
                                    items={campaign.categories}
                                    color="bg-blue-50 text-blue-700"
                                    emptyText="No categories"
                                />
                            </SectionBlock>
                            <SectionBlock title="Sub Categories">
                                <ChipGroup
                                    items={campaign.subcategories}
                                    color="bg-purple-50 text-purple-700"
                                    emptyText="No sub categories"
                                />
                            </SectionBlock>
                            <SectionBlock title="Campaign Names">
                                {visibleCampaigns.length ? (
                                    <>
                                        <div className="flex flex-wrap gap-1.5">
                                            {visibleCampaigns.map((name) => (
                                                <Chip
                                                    key={name}
                                                    text={name}
                                                    color="bg-indigo-50 text-indigo-700"
                                                />
                                            ))}
                                        </div>
                                        {(campaign.campaign_names || []).length > 8 && (
                                            <button
                                                onClick={() => setShowAllCampaigns(!showAllCampaigns)}
                                                className="mt-3 text-xs font-semibold text-indigo-600 transition hover:text-indigo-800"
                                            >
                                                {showAllCampaigns
                                                    ? "Show Less"
                                                    : `View ${(campaign.campaign_names || []).length - 8} More`}
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-sm text-gray-400">No campaigns</span>
                                )}
                            </SectionBlock>
                        </div>

                        {/* Right column */}
                        <div className="lg:col-span-8 p-6 space-y-5">

                            <SectionBlock title="Attachments">
                                {campaign.attachments?.length ? (
                                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                                        {campaign.attachments.map((file) => (
                                            <a
                                                key={file}
                                                href={file}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="overflow-hidden rounded-xl border bg-white transition hover:shadow-md"
                                            >
                                                {isImage(file) ? (
                                                    <>
                                                        <img
                                                            src={file}
                                                            alt=""
                                                            className="h-32 w-full object-cover"
                                                        />

                                                        <div className="p-2">
                                                            <p className="truncate text-xs text-gray-600">
                                                                {getFileName(file)}
                                                            </p>
                                                        </div>
                                                    </>
                                                ) : isPdf(file) ? (
                                                    <div className="flex h-32 flex-col items-center justify-center bg-red-50">
                                                        <div className="text-5xl">
                                                            📄
                                                        </div>

                                                        <div className="mt-2 px-2 text-center text-xs text-gray-700 line-clamp-2">
                                                            {getFileName(file)}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex h-32 items-center justify-center text-sm">
                                                        {getFileName(file)}
                                                    </div>
                                                )}
                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-sm text-gray-400">
                                        No attachments
                                    </span>
                                )}
                            </SectionBlock>

                            <SectionBlock title="Email Preview">
                                <div
                                    className="border border-gray-100 rounded-lg bg-white p-5 min-h-[280px] max-h-[420px] overflow-y-auto prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{
                                        __html: campaign.body ||
                                            "<p class='text-gray-400'>No email body available.</p>"
                                    }}
                                />
                            </SectionBlock>

                        </div>

                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end shrink-0 bg-gray-50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-black transition"
                    >
                        Close
                    </button>
                </div>

            </div>
        </div>
    );
};

export default CampaignDetailsModal;